import { isGrantFieldEmpty } from './grantFieldMerge.js';
import { isLikelyRdTitle } from './rdTitleMatch.js';
import { JOB_POSTINGS_FIELD, parseJsonArray, normalizeOpenings } from './companyJobPostings.js';

const DESCRIPTION_MAX = 800;
const LONG_TEXT_MAX = 400;
const JOB_OPENINGS_MAX = 10;
const TEAM_RD_TITLES_MAX = 8;

export const PRODUCTION_REQUIRED_FIELDS = [
	'Name', 'Description', 'GrantUrl', 'Amount', 'Deadline',
	'Eligibility_Criteria', 'ExcitingBenefitContent',
	'Eligible_Industry', 'Eligible_Region', 'Eligible_Employees', 'Eligible_Founded', 'Eligible_Revenue',
];

const PRODUCTION_MERGE_FIELDS = new Set([
	...PRODUCTION_REQUIRED_FIELDS,
	'Description_2',
	'Eligible_Activities',
	'Eligible_Applicants',
	'Grant_Goal',
	'Category',
	'Eligible_Profitable',
	'GrantOrLoan',
	'OpenOrClosed',
	'Source',
	'LastScrapedAt',
	'ScrapeStatus',
]);

const REGION_MAP = {
	CA: 'Canada',
	US: 'United States',
	UK: 'United Kingdom',
	AU: 'Australia',
	DE: 'Germany',
	FR: 'France',
	IT: 'Italy',
	ES: 'Spain',
	NL: 'Netherlands',
	SE: 'Sweden',
	NO: 'Norway',
	DK: 'Denmark',
	FI: 'Finland',
	CH: 'Switzerland',
	AT: 'Austria',
	BE: 'Belgium',
	IE: 'Ireland',
	PT: 'Portugal',
	GR: 'Greece',
	PL: 'Poland',
	CZ: 'Czech Republic',
	HU: 'Hungary',
	RO: 'Romania',
	BG: 'Bulgaria',
	HR: 'Croatia',
	SI: 'Slovenia',
	SK: 'Slovakia',
	LT: 'Lithuania',
	LV: 'Latvia',
	EE: 'Estonia',
	CY: 'Cyprus',
	MT: 'Malta',
	LU: 'Luxembourg',
};

export function formatRegion(region) {
	return REGION_MAP[region] || region;
}

/** Airtable allows max 10 records per create request. */
export async function createAirtableInBatches(base, tableName, records, batchSize = 10) {
	if (!records?.length) return [];
	const created = [];
	for (let i = 0; i < records.length; i += batchSize) {
		const batch = records.slice(i, i + batchSize);
		const result = await base(tableName).create(batch);
		created.push(...result);
	}
	return created;
}

export async function updateAirtableInBatches(base, tableName, records, batchSize = 10) {
	if (!records?.length) return [];
	const updated = [];
	for (let i = 0; i < records.length; i += batchSize) {
		const batch = records.slice(i, i + batchSize);
		const result = await base(tableName).update(batch);
		updated.push(...result);
	}
	return updated;
}

export function normalizeUrlForDedup(url) {
	try {
		const u = new URL(url);
		if (!['http:', 'https:'].includes(u.protocol)) return '';
		const hostname = u.hostname.toLowerCase().replace(/^www\./, '');
		const pathname = u.pathname.replace(/\/+$/, '').toLowerCase() || '/';
		return hostname + pathname;
	} catch {
		return '';
	}
}

export function normalizeGrantName(name) {
	return (name || '')
		.toString()
		.toLowerCase()
		.replace(/&/g, ' and ')
		.replace(/[^a-z0-9]+/g, ' ')
		.trim()
		.replace(/\s+/g, ' ');
}

export function productionGrantKey(fields) {
	const url = normalizeUrlForDedup(fields?.GrantUrl || '');
	if (url) return `url:${url}`;
	const name = normalizeGrantName(fields?.Name);
	return name ? `name:${name}` : '';
}

export function grantsMatchByCanonicalIdentity(firstFields = {}, secondFields = {}) {
	const firstUrl = normalizeUrlForDedup(firstFields.GrantUrl);
	const secondUrl = normalizeUrlForDedup(secondFields.GrantUrl);
	if (firstUrl && secondUrl) return firstUrl === secondUrl;

	const firstName = normalizeGrantName(firstFields.Name);
	const secondName = normalizeGrantName(secondFields.Name);
	return !!firstName && firstName === secondName;
}

export function hasAllProductionRequiredFields(fields) {
	if (!fields) return false;
	if (!normalizeUrlForDedup(fields.GrantUrl)) return false;
	const amount = Number(fields.Amount);
	if (isNaN(amount) || amount < 1000) return false;
	for (const key of PRODUCTION_REQUIRED_FIELDS) {
		if (isGrantFieldEmpty(fields[key])) return false;
	}
	return true;
}

export function toProductionGrantFields(fields = {}) {
	const productionFields = {};
	for (const [key, value] of Object.entries(fields)) {
		if (!PRODUCTION_MERGE_FIELDS.has(key) || isGrantFieldEmpty(value)) continue;
		productionFields[key] = key === 'Amount' ? Number(value) : value;
	}
	return productionFields;
}

export function normalizeResearchResultToFields(result, company, researchPromptVal, researchModelVal) {
	const name = (result.name || result.title || '').trim();
	const amountNum = Number(result.amount);
	const amount = Number.isFinite(amountNum) && amountNum > 0 ? amountNum : undefined;
	const deadline = (result.deadline || '').trim();
	const grantUrl = (result.url || result.link || '').trim();
	const fields = {
		Name: name,
		Deadline: deadline,
		GrantUrl: grantUrl,
		Description: result.description || result.summary || '',
		Eligibility_Criteria: result.eligibility || result.criteria || '',
		ExcitingBenefitContent: result.benefit || result.excitingBenefitContent || '',
		Eligible_Industry: result.industry || '',
		Eligible_Region: result.region || '',
		Eligible_Employees: result.employeeCount ?? '',
		Eligible_Founded: result.foundedYear ?? result.requiredYearsInOperation ?? result.yearsInOperation ?? '',
		Eligible_Revenue: result.eligibleRevenue || result.eligible_revenue || '',
		Category: result.category || '',
		GrantOrLoan: result.type || '',
		Source: 'Deep Research',
		ResearchPrompt: researchPromptVal ?? '',
		ResearchModel: researchModelVal ?? '',
		CompanyID: company?.id ?? '',
	};
	if (amount !== undefined) fields.Amount = amount;
	return fields;
}

function addNamesFromField(excludedNames, fieldValue) {
	if (!fieldValue || typeof fieldValue !== 'string') return;
	fieldValue
		.split(',')
		.map((name) => name.trim())
		.filter(Boolean)
		.forEach((name) => excludedNames.add(name));
}

/** Build excluded grant names from company lists + matching grants already eligible for the company. */
export function getExcludedGrantNames(company, matchingGrants = []) {
	if (!company) return [];
	const excludedNames = new Set();

	addNamesFromField(excludedNames, company.fields?.InterestedIn);
	addNamesFromField(excludedNames, company.fields?.ExcludeGrants);
	addNamesFromField(excludedNames, company.fields?.AIRecommended);
	addNamesFromField(excludedNames, company.fields?.GovernmentGrantsPursued);

	if (matchingGrants?.length > 0) {
		const companyCountry = (company.fields?.Country || '').toLowerCase().trim();
		const companyProvince = (company.fields?.ProvinceOrState || '').toLowerCase().trim();
		const companyIndustry = (company.fields?.Industry || '').toLowerCase().trim();
		const companyEmployees = parseInt(company.fields?.Employees, 10) || 0;

		matchingGrants.forEach((grant) => {
			const grantName = grant.fields?.Name;
			if (!grantName) return;

			const grantRegion = (grant.fields?.Eligible_Region || grant.fields?.Region || '').toLowerCase();
			const grantIndustry = (grant.fields?.Eligible_Industry || grant.fields?.Industry || '').toLowerCase();
			const grantMinEmployees = parseInt(grant.fields?.Eligible_Employees_Min, 10) || 0;
			const grantMaxEmployees = parseInt(grant.fields?.Eligible_Employees_Max, 10) || 999999;

			const regionMatches =
				!grantRegion ||
				grantRegion === 'all' ||
				grantRegion.includes('all') ||
				grantRegion.includes(companyCountry) ||
				(companyProvince && grantRegion.includes(companyProvince));

			const industryMatches =
				!grantIndustry ||
				grantIndustry === 'all' ||
				grantIndustry.includes('all') ||
				grantIndustry.includes(companyIndustry) ||
				companyIndustry.includes(grantIndustry);

			const employeesMatch =
				companyEmployees >= grantMinEmployees && companyEmployees <= grantMaxEmployees;

			if (regionMatches && industryMatches && employeesMatch) {
				excludedNames.add(grantName);
			}
		});
	}

	return Array.from(excludedNames);
}

/** Toggleable context blocks assembled into the API prompt (not the editable textarea). */
export const CONTEXT_SECTION_DEFS = [
	{
		id: 'profile',
		label: 'Company profile',
		hint: 'Name, industry, size, location, description',
		defaultOn: true,
	},
	{
		id: 'priorities',
		label: 'Priorities & expenses',
		hint: 'Strategic priorities, tech, expenses, program interests',
		defaultOn: true,
	},
	{
		id: 'eligibility',
		label: 'Eligibility facts',
		hint: 'Revenue, profitability, CCPC, company type',
		defaultOn: true,
	},
	{
		id: 'hiring',
		label: 'Hiring & open roles',
		hint: 'Planned hires and open job titles',
		defaultOn: true,
	},
	{
		id: 'team',
		label: 'Team summary',
		hint: 'Headcount and likely R&D roles (no salaries)',
		defaultOn: true,
	},
	{
		id: 'rd',
		label: 'R&D signals',
		hint: 'Reported R&D spend from the company profile',
		defaultOn: true,
	},
	{
		id: 'exclusions',
		label: 'Exclusions',
		hint: 'Grants already on roadmap or declined',
		defaultOn: true,
	},
];

export function getDefaultContextSections() {
	return Object.fromEntries(CONTEXT_SECTION_DEFS.map((s) => [s.id, s.defaultOn]));
}

function hasMeaningfulValue(value) {
	if (value == null) return false;
	if (typeof value === 'number') return Number.isFinite(value);
	if (typeof value === 'boolean') return true;
	if (Array.isArray(value)) return value.length > 0;
	const trimmed = String(value).trim();
	return trimmed !== '' && trimmed.toLowerCase() !== 'n/a';
}

function truncateText(value, max = LONG_TEXT_MAX) {
	const text = String(value || '').trim();
	if (!text) return '';
	if (text.length <= max) return text;
	return `${text.slice(0, max - 1).trimEnd()}…`;
}

function formatMoney(value) {
	if (value == null || value === '') return null;
	const num = typeof value === 'number' ? value : Number(String(value).replace(/[^0-9.-]/g, ''));
	if (!Number.isFinite(num)) return String(value);
	return `$${num.toLocaleString()}`;
}

function formatFieldList(value) {
	if (!hasMeaningfulValue(value)) return null;
	if (Array.isArray(value)) return value.filter(Boolean).join(', ');
	const raw = String(value).trim();
	try {
		const parsed = JSON.parse(raw);
		if (Array.isArray(parsed)) return parsed.filter(Boolean).join(', ');
	} catch {
		/* plain string */
	}
	return raw;
}

function line(label, value) {
	if (!hasMeaningfulValue(value)) return null;
	return `${label}: ${value}`;
}

/** Short research intent for the editable textarea — company facts stay in context chips. */
export function buildResearchIntent(company) {
	if (!company?.fields) return '';
	const f = company.fields;
	const countryName = formatRegion(f.Country) || 'their region';
	const industry = f.Industry || 'their';
	return `Find grants for ${f.Name || 'this company'} that support ${industry} businesses in ${countryName}, especially technology adoption, innovation, R&D, hiring, and growth. Prefer ongoing or rolling programs. Use only direct official application URLs.`;
}

/** @deprecated Use buildResearchIntent — kept for callers that still import the old name. */
export function buildDefaultPrompt(company) {
	return buildResearchIntent(company);
}

/**
 * @deprecated Prefer intent textarea + context chips. Returns intent only (no company dump).
 */
export function buildEnhancedPrompt(company) {
	return buildResearchIntent(company);
}

export function getCompanyJobOpenings(company) {
	return normalizeOpenings(parseJsonArray(company?.fields?.[JOB_POSTINGS_FIELD]));
}

export function buildTeamSummaryLines(teamMembers = []) {
	const members = Array.isArray(teamMembers) ? teamMembers.filter(Boolean) : [];
	if (members.length === 0) return [];

	const rdMembers = members.filter((m) => {
		if (typeof m.rdPct === 'number' && m.rdPct >= 30) return true;
		return isLikelyRdTitle(m.title || m.matchedTitle || '');
	});

	const rdTitles = [
		...new Set(
			rdMembers
				.map((m) => String(m.title || m.matchedTitle || '').trim())
				.filter(Boolean)
		),
	].slice(0, TEAM_RD_TITLES_MAX);

	const lines = [
		`Team size on file: ${members.length}`,
		`Likely R&D / technical roles: ${rdMembers.length}`,
	];
	if (rdTitles.length > 0) {
		lines.push(`Sample R&D titles: ${rdTitles.join('; ')}`);
	}
	return lines;
}

export function buildJobOpeningsSummaryLines(openings = []) {
	const list = Array.isArray(openings) ? openings : [];
	if (list.length === 0) return [];

	const rdCount = list.filter((o) => o.isRd || isLikelyRdTitle(o.title)).length;
	const sample = list.slice(0, JOB_OPENINGS_MAX).map((o) => {
		const rd = o.isRd || isLikelyRdTitle(o.title) ? ' (likely R&D)' : '';
		const loc = o.location ? ` — ${o.location}` : '';
		return `${o.title}${rd}${loc}`;
	});

	return [
		`Open roles indexed: ${list.length} (${rdCount} likely R&D)`,
		...sample.map((s) => `Open role: ${s}`),
		list.length > JOB_OPENINGS_MAX
			? `…and ${list.length - JOB_OPENINGS_MAX} more openings`
			: null,
	].filter(Boolean);
}

function buildProfileLines(f) {
	const countryName = formatRegion(f.Country);
	const description =
		truncateText(f.Description, DESCRIPTION_MAX) ||
		truncateText(f.LongDescription, DESCRIPTION_MAX);

	return [
		line('Name', f.Name),
		line('Industry', f.Industry),
		line('Employees', f.Employees),
		line('Country', countryName),
		line('Province/State', f.ProvinceOrState),
		line('Founded', f.Founded),
		line('Domain', f.Domain),
		line('LinkedIn', f.LinkedInUrl),
		description ? `Description: ${description}` : null,
	].filter(Boolean);
}

function buildPrioritiesLines(f) {
	return [
		line('Strategic Priorities', truncateText(f.StrategicPriorities)),
		line('Technologies', truncateText(f.Technologies)),
		line('Government Programs Interest', formatFieldList(f.GovernmentProgramsInterest)),
		line('Interested Opportunities', formatFieldList(f.InterestedOpportunities)),
		line('Top Expenses (12 mo)', truncateText(f.TopExpenses)),
		line('Expensive Vendors', truncateText(f.ExpensiveVendors, 240)),
		line('Likely Customers', truncateText(f.LikelyCustomers, 240)),
		line('Additional Info', truncateText(f.AdditionalInfo)),
	].filter(Boolean);
}

function buildEligibilityLines(f) {
	return [
		line('Annual Revenue', formatMoney(f.AnnualRevenue)),
		line('ARR', formatMoney(f.AnnualRecurringRevenue)),
		line('MRR', formatMoney(f.MonthlyRecurringRevenue)),
		line('Profitable', f['Profitable?']),
		line('Company Type', f.CompanyType),
		line('CCPC Status', f.CCPCStatus),
		line('Legal Name', f.LegalName),
	].filter(Boolean);
}

function buildHiringLines(f, openings = []) {
	const lines = [
		line('Planned Full-Time Hires', f.PlannedHires),
		// HiringPlan is often a spreadsheet URL — only include if it looks like prose.
		f.HiringPlan && !/^https?:\/\//i.test(String(f.HiringPlan).trim())
			? line('Hiring Plan', truncateText(f.HiringPlan))
			: null,
		...buildJobOpeningsSummaryLines(openings),
	].filter(Boolean);
	return lines;
}

function buildRdLines(f) {
	return [
		line('R&D Last Year', formatMoney(f['R&DLastYear'] ?? f.RDLastYear)),
		line('R&D This Year', formatMoney(f['R&DThisYear'] ?? f.RDThisYear)),
		line('Government Grants Pursued', truncateText(f.GovernmentGrantsPursued, 240)),
	].filter(Boolean);
}

/**
 * Which context chips are available for this company (+ optional team roster).
 * @returns {Array<{ id: string, label: string, hint: string, defaultOn: boolean, available: boolean, count?: number, preview: string }>}
 */
export function getAvailableContextSections(
	company,
	{ teamMembers = [], matchingGrants = [] } = {}
) {
	const f = company?.fields || {};
	const openings = getCompanyJobOpenings(company);
	const excluded = getExcludedGrantNames(company, matchingGrants);
	const teamLines = buildTeamSummaryLines(teamMembers);

	const contentById = {
		profile: buildProfileLines(f),
		priorities: buildPrioritiesLines(f),
		eligibility: buildEligibilityLines(f),
		hiring: buildHiringLines(f, openings),
		team: teamLines,
		rd: buildRdLines(f),
		exclusions: excluded,
	};

	return CONTEXT_SECTION_DEFS.map((def) => {
		const content = contentById[def.id] || [];
		// Exclusions are always available (may be an empty list).
		const available = def.id === 'exclusions' ? !!company : content.length > 0;
		let preview = '';
		if (def.id === 'exclusions') {
			preview =
				content.length > 0
					? `${content.length} grant${content.length === 1 ? '' : 's'} excluded`
					: 'None yet';
		} else if (def.id === 'team') {
			preview = available ? content[0] : 'Load when available';
		} else if (def.id === 'hiring') {
			preview = available
				? openings.length > 0
					? `${openings.length} open roles`
					: content[0]
				: '';
		} else {
			preview = available ? `${content.length} fields` : '';
		}
		return {
			...def,
			available,
			count: content.length,
			preview,
		};
	});
}

/**
 * Assemble enabled context blocks for the model. Call recordings / Drive are intentionally omitted.
 */
export function buildCompanyContextBlocks(
	company,
	{
		matchingGrants = [],
		teamMembers = [],
		enabledSections = getDefaultContextSections(),
	} = {}
) {
	const f = company?.fields || {};
	const openings = getCompanyJobOpenings(company);
	const excluded = getExcludedGrantNames(company, matchingGrants);
	const blocks = [];

	const pushBlock = (id, title, lines) => {
		if (!enabledSections[id] || !lines?.length) return;
		blocks.push(`--- ${title} ---\n${lines.map((l) => `- ${l}`).join('\n')}`);
	};

	pushBlock('profile', 'COMPANY PROFILE', buildProfileLines(f));
	pushBlock('priorities', 'PRIORITIES & EXPENSES', buildPrioritiesLines(f));
	pushBlock('eligibility', 'ELIGIBILITY FACTS', buildEligibilityLines(f));
	pushBlock('hiring', 'HIRING & OPEN ROLES', buildHiringLines(f, openings));
	pushBlock('team', 'TEAM SUMMARY', buildTeamSummaryLines(teamMembers));
	pushBlock('rd', 'R&D SIGNALS', buildRdLines(f));

	if (enabledSections.exclusions) {
		blocks.push(
			`--- EXCLUSION LIST (DO NOT suggest these; company already has them or has declined) ---\n${
				excluded.length > 0
					? excluded.map((name) => `- ${name}`).join('\n')
					: '- None'
			}`
		);
	}

	return { blocks, excludedGrants: excluded };
}

/**
 * Full API prompt: structured context + user intent + research instructions.
 * Does not re-dump company fields into the intent — intent is treated as focus only.
 */
export function buildDeepResearchApiPrompt(
	company,
	researchPrompt,
	matchingGrants = [],
	{
		teamMembers = [],
		enabledSections = getDefaultContextSections(),
	} = {}
) {
	const companyFields = company?.fields || {};
	const countryName = formatRegion(companyFields.Country);
	const industry = companyFields.Industry || 'their industry';
	const intent =
		(researchPrompt && String(researchPrompt).trim()) || buildResearchIntent(company);

	const { blocks, excludedGrants } = buildCompanyContextBlocks(company, {
		matchingGrants,
		teamMembers,
		enabledSections,
	});

	let enhancedPrompt = `Find grants and funding opportunities for ${companyFields.Name || 'this company'}${
		companyFields.Industry ? `, a ${companyFields.Industry} company` : ''
	}${companyFields.Employees != null ? ` with ${companyFields.Employees} employees` : ''}${
		countryName ? ` in ${countryName}` : ''
	}.

${blocks.join('\n\n')}

--- RESEARCH FOCUS ---
${intent}

--- WHAT TO FIND ---
1. Government grants for ${industry} businesses${countryName ? ` in ${countryName}` : ''}
2. Innovation and technology adoption grants
3. Small business and startup funding programs
4. Industry-specific grants for ${industry}
5. Regional economic development grants
6. Research and development funding opportunities
7. Export and international business grants
8. Digital transformation and technology grants`;

	if (enabledSections.priorities && (companyFields.TopExpenses || companyFields.StrategicPriorities)) {
		enhancedPrompt += `\n9. Grants that align with the company's top expenses and strategic priorities`;
	}
	if (enabledSections.hiring || enabledSections.team) {
		enhancedPrompt += `\n10. Hiring, wage subsidy, training, and talent programs when the company is hiring or has R&D headcount`;
	}

	enhancedPrompt += `

--- CRITERIA ---
- Prefer and emphasize grants that are ongoing, rolling, or continuously accepting applications (e.g. "Ongoing", "Rolling deadline", "Open year-round"). Put these first when possible.
- Only include grants that are currently available and accepting applications.
- Suitable for ${companyFields.Employees ?? 'this size'} employee companies, relevant to ${industry}${
		countryName ? `, available in ${countryName}` : ''
	}.`;

	if (enabledSections.priorities && companyFields.TopExpenses) {
		enhancedPrompt += `\n- Where relevant, prioritize grants that can help fund the company's top expenses.`;
	}
	if (enabledSections.priorities && companyFields.StrategicPriorities) {
		enhancedPrompt += `\n- Support the company's strategic priorities where possible.`;
	}

	enhancedPrompt += `

--- URL RULE (critical) ---
For each grant, the "url" field MUST be the direct, official grant application or program page where the applicant can apply. Do NOT use:
- Aggregator or directory sites
- News articles, overviews, or "best grants" listicles
- Ad or pay-per-click pages
- Third-party info sites
Use only official government, foundation, or program websites with a clear application link.

--- OUTPUT ---
Return exactly 10-30 NEW grant opportunities (aim for 20-25). Do not include any grant from the EXCLUSION LIST. Return ONLY a JSON array, no text before or after.

Every grant MUST use verified program information. Never infer eligibility from the company profile and never invent a value. Use null when a field cannot be verified:
- name: string (grant/program name)
- description: string (detailed description of the grant)
- url: string (direct official application or program page; no aggregators or news articles)
- amount: number or null (e.g. 50000 for $50,000)
- deadline: string or null (e.g. "2024-12-31", "Ongoing", "Rolling")
- eligibility: string (eligibility criteria)
- benefit: string (e.g. "50% of eligible costs", "Up to $X for Y", "Funding for marketing and travel")
- industry: string (or comma-separated industries)
- region: string (or comma-separated regions)
- employeeCount: string, number, or null (e.g. "1-50", "10+")
- foundedYear: string or null (e.g. "No requirement", "2+ years", or year)
- eligibleRevenue: string or null (min revenue requirement, e.g. "$500,000" or "No minimum")

JSON array format (fill every field for each grant):
[
	{
		"name": "Grant Name",
		"description": "Detailed description of the grant and what it funds.",
		"url": "https://official-program.gov/apply",
		"amount": 50000,
		"deadline": "Ongoing",
		"eligibility": "Specific eligibility criteria.",
		"benefit": "50% of eligible costs, up to $X for Y.",
		"industry": "Industries explicitly listed by the program",
		"region": "Regions explicitly listed by the program",
		"employeeCount": "1-50",
		"foundedYear": "No requirement",
		"eligibleRevenue": "No minimum",
		"category": "Government Grant",
		"type": "Grant"
	}
]`;

	return { enhancedPrompt, excludedGrants };
}

export function parseResearchJsonContent(content, fallbackCompany) {
	let allGrants = [];
	try {
		let jsonContent = content;
		const jsonMatch = content.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
		if (jsonMatch) {
			jsonContent = jsonMatch[1].trim();
		}

		const parsedContent = JSON.parse(jsonContent);
		if (Array.isArray(parsedContent) && parsedContent.length > 0) {
			allGrants = parsedContent;
		} else if (parsedContent && typeof parsedContent === 'object') {
			allGrants = [parsedContent];
		} else {
			throw new Error('Invalid JSON format');
		}
	} catch (parseError) {
		console.error('JSON parsing error:', parseError);
		try {
			const jsonObjects = content.match(/\{[^{}]*\}/g);
			if (jsonObjects && jsonObjects.length > 0) {
				allGrants = jsonObjects
					.map((obj) => {
						try {
							return JSON.parse(obj);
						} catch {
							return null;
						}
					})
					.filter((obj) => obj !== null);
			}
			if (allGrants.length === 0) {
				throw new Error('No valid JSON objects found');
			}
		} catch (fallbackError) {
			console.error('Fallback parsing also failed:', fallbackError);
			allGrants = [
				{
					name: `Research Grant for ${fallbackCompany?.fields?.Name || 'Company'}`,
					amount: 25000,
					deadline: '2024-12-31',
					url: '',
					description: content.substring(0, 1000),
					eligibility: 'Check eligibility requirements',
					industry: fallbackCompany?.fields?.Industry,
					region: fallbackCompany?.fields?.Country,
					employeeCount: fallbackCompany?.fields?.Employees,
					category: 'Research',
					type: 'Grant',
				},
			];
		}
	}
	return allGrants;
}

export const DEEP_RESEARCH_MODEL_OPTIONS = [
	{ value: 'gpt-5.2-2025-12-11', label: 'GPT-5.2 (Recommended)' },
	{ value: 'gpt-5-nano-2025-08-07', label: 'GPT-5 Nano' },
	{ value: 'gpt-5.2', label: 'GPT-5.2' },
	{ value: 'gpt-5', label: 'GPT-5' },
	{ value: 'gpt-5-mini', label: 'GPT-5 Mini' },
	{ value: 'gpt-4o-search-preview', label: 'GPT-4o + Web Search' },
	{ value: 'gpt-4o', label: 'GPT-4o' },
	{ value: 'claude-opus-4', label: 'Claude Opus 4 (Latest)' },
	{ value: 'claude-sonnet-4', label: 'Claude Sonnet 4 (Latest)' },
	{ value: 'claude-haiku-4', label: 'Claude Haiku 4 (Latest)' },
	{ value: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet' },
	{ value: 'claude-3-opus-20240229', label: 'Claude 3 Opus' },
	{ value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash' },
	{ value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' },
];

export function getResearchModelLabel(value) {
	return DEEP_RESEARCH_MODEL_OPTIONS.find((o) => o.value === value)?.label || value;
}

export function namesSetFromField(fieldValue) {
	const names = (fieldValue || '')
		.split(',')
		.map((name) => name.trim())
		.filter(Boolean);
	return new Set(names);
}
